import { useEffect, useState } from "react";
import { Plus, ShieldCheck, Trash2, Crown, Mail } from "lucide-react";
import { toast } from "sonner";
import { ApiError } from "@/lib/api";
import { ROLE_LABELS, TeamMember, teamApi, timeAgo, WorkspaceRole } from "@/lib/catalog-api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/catalog/Skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui-shim";

export default function TeamSettings() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [members, setMembers] = useState<TeamMember[] | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [transferTarget, setTransferTarget] = useState<TeamMember | null>(null);

  async function reload() {
    setMembers(null);
    try {
      const list = await teamApi.list();
      setMembers(list);
    } catch (e: any) {
      toast.error(e instanceof ApiError ? e.message : "Couldn't load team.");
      setMembers([]);
    }
  }
  useEffect(() => { reload(); }, []);

  async function changeRole(m: TeamMember, role: "admin" | "member") {
    try {
      await teamApi.changeRole(m.id, role);
      toast.success(`${m.email} is now ${role}.`);
      reload();
    } catch (e: any) {
      toast.error(e instanceof ApiError ? e.message : "Could not change role.");
    }
  }

  async function remove(m: TeamMember) {
    if (!confirm(`Remove ${m.email} from the team?`)) return;
    try {
      await teamApi.remove(m.id);
      toast.success(`${m.email} removed.`);
      reload();
    } catch (e: any) {
      toast.error(e instanceof ApiError ? e.message : "Could not remove.");
    }
  }

  return (
    <div data-testid="team-settings" className="space-y-8">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-xl">Team</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Invite collaborators. Members catalog and pull. Admins can also import and reorganise.
          </p>
        </div>
        <Button onClick={() => setShowInvite(true)}
          data-testid="invite-btn"
          className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-10">
          <Plus className="w-4 h-4 mr-2" /> Invite teammate
        </Button>
      </div>

      {!members ? (
        <Skeleton className="h-40" />
      ) : members.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">Just you for now.</p>
      ) : (
        <ul className="border border-border divide-y divide-border bg-background" data-testid="team-list">
          {members.map((m) => (
            <li key={m.id} className="grid grid-cols-[1fr_auto] gap-3 px-4 py-3.5 items-center"
              data-testid={`team-member-${m.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">{m.full_name || m.email}</span>
                  {m.role === "owner" && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-oxblood">
                      <Crown className="w-3 h-3" /> Owner
                    </span>
                  )}
                  {m.status === "pending" && (
                    <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground border border-border px-1.5 py-0.5">
                      Pending
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  {m.email}
                  {m.invited_at ? ` · invited ${timeAgo(m.invited_at)}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {m.role !== "owner" && (
                  <RoleSelect role={m.role} onChange={(r) => changeRole(m, r)} disabled={!isOwner && m.role === "admin"} />
                )}
                {isOwner && m.role !== "owner" && m.status === "active" && (
                  <Button variant="outline"
                    onClick={() => setTransferTarget(m)}
                    data-testid={`transfer-${m.id}`}
                    className="rounded-none border-foreground/20 hover:bg-bone h-9 text-xs">
                    Make owner
                  </Button>
                )}
                {m.role !== "owner" && (
                  <button type="button" onClick={() => remove(m)}
                    data-testid={`remove-${m.id}`}
                    aria-label={`Remove ${m.email}`}
                    className="text-muted-foreground hover:text-oxblood p-2">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {showInvite && (
        <InviteDialog onClose={() => setShowInvite(false)} onInvited={reload} />
      )}
      {transferTarget && (
        <TransferDialog target={transferTarget}
          onClose={() => setTransferTarget(null)}
          onDone={reload} />
      )}
    </div>
  );
}

function RoleSelect({ role, onChange, disabled }: {
  role: WorkspaceRole; onChange: (r: "admin" | "member") => void; disabled?: boolean;
}) {
  return (
    <select value={role} disabled={disabled}
      data-testid="role-select"
      onChange={(e) => onChange(e.target.value as "admin" | "member")}
      className="h-9 px-2 border border-border bg-background text-sm rounded-none disabled:opacity-50">
      <option value="member">{ROLE_LABELS.member}</option>
      <option value="admin">{ROLE_LABELS.admin}</option>
    </select>
  );
}

function InviteDialog({ onClose, onInvited }: { onClose: () => void; onInvited: () => void }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "member">("member");
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await teamApi.invite({ email, role });
      toast.success(`Invite sent to ${email}.`);
      onInvited();
      onClose();
    } catch (e: any) {
      toast.error(e instanceof ApiError ? e.message : "Could not send invite.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none border-border" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Mail className="w-5 h-5 text-oxblood" /> Invite teammate
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label htmlFor="invite-email" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Email
            </Label>
            <Input id="invite-email" type="email" required
              data-testid="invite-email"
              value={email} onChange={(e: any) => setEmail(e.target.value)}
              placeholder="amelia@studio.com"
              className="mt-1.5 h-10 rounded-none" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Role</Label>
            <div className="mt-1.5 flex gap-2">
              {(["member", "admin"] as const).map((r) => (
                <button type="button" key={r}
                  onClick={() => setRole(r)}
                  data-testid={`role-${r}`}
                  className={`px-4 py-2 text-sm border ${
                    role === r
                      ? "border-oxblood bg-oxblood/5 text-oxblood"
                      : "border-border hover:bg-bone"
                  }`}>
                  {ROLE_LABELS[r]}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {role === "admin"
                ? "Admins can invite, import, edit settings, and run reports."
                : "Members can catalog, move, and pull — no settings access."}
            </p>
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose}
              className="rounded-none border-foreground/20 hover:bg-bone h-10">Cancel</Button>
            <Button type="submit" disabled={busy}
              data-testid="invite-send"
              className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-10">
              {busy ? "Sending…" : "Send invite"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TransferDialog({ target, onClose, onDone }: {
  target: TeamMember; onClose: () => void; onDone: () => void;
}) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!target.user_id) {
      toast.error("Member hasn't accepted yet — can't transfer.");
      return;
    }
    setBusy(true);
    try {
      await teamApi.transferOwnership({ to_user_id: target.user_id, password });
      toast.success(`${target.email} is now owner.`);
      onDone();
      onClose();
    } catch (e: any) {
      toast.error(e instanceof ApiError ? e.message : "Could not transfer.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none border-border" data-testid="transfer-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-oxblood" /> Transfer ownership
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground mt-2">
          You'll be downgraded to admin. {target.email} will become the new owner with full control.
        </p>
        <form onSubmit={submit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="xfer-pwd" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Confirm with your password
            </Label>
            <Input id="xfer-pwd" type="password" required
              data-testid="transfer-password"
              value={password} onChange={(e: any) => setPassword(e.target.value)}
              className="mt-1.5 h-10 rounded-none" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}
              className="rounded-none border-foreground/20 hover:bg-bone h-10">Cancel</Button>
            <Button type="submit" disabled={busy}
              data-testid="transfer-confirm"
              className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-10">
              {busy ? "Transferring…" : "Transfer ownership"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
